import React, { SelectHTMLAttributes } from "react";
import { cn } from "@/lib/utils";
import { useWidgetColor } from "@/hooks/useWidgetColor";

interface CustomSelectProps extends SelectHTMLAttributes<HTMLSelectElement> {
  options: { value: string; label: string }[];
  placeholder?: string;
}

const CustomSelect = React.forwardRef<HTMLSelectElement, CustomSelectProps>(
  ({ className, options, placeholder, ...props }, ref) => {
    const selectStyles = cn(
      "flex h-12 w-full rounded-md bg-background px-3 py-2 text-sm",
      "file:border-0 file:bg-transparent file:text-sm file:font-medium",
      "placeholder:text-muted-foreground",
      "focus-visible:outline-none",
      "disabled:cursor-not-allowed disabled:opacity-50",
      "border border-gray-300",
      "appearance-none",
      "-webkit-appearance-none",
      "focus:border-[var(--ring-color)] hover:border-[var(--ring-color)] focus:border-2 hover:border-2",
    );
    const widgetColor = useWidgetColor();

    return (
      <div className="relative">
        <select
          className={cn(selectStyles, className)}
          ref={ref}
          style={{ "--ring-color": widgetColor } as React.CSSProperties}
          {...props}
        >
          {placeholder && <option value="">{placeholder}</option>}
          {options.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
        {/* Add custom caret/icon here */}
        <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-700">
          <svg
            className="fill-current h-4 w-4"
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 20 20"
          >
            <path d="M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z" />
          </svg>
        </div>
      </div>
    );
  },
);

CustomSelect.displayName = "CustomSelect";

export { CustomSelect };
